import { Message, ToolCardChip, ToolCardChoices, ToolCardImage, ToolCardJob, ToolCardKind, ToolCardProposal, ToolCardVideo } from '../../types/chat';

export interface SearchResult {
  index: number;
  title: string;
  url: string;
  snippet: string;
}

/**
 * チャットツール実行の SSE イベント（tool_use / tool_result）。
 */
export interface ToolStreamEvent {
  type: 'tool_use' | 'tool_result';
  call_id: string;
  name: string;
  label?: string;
  args_summary?: string | null;
  status?: 'running' | 'progress';
  progress?: number | null;
  ok?: boolean;
  summary?: string | null;
  job_id?: string | null;
  images?: ToolCardImage[];
  kind?: ToolCardKind;
  videos?: ToolCardVideo[];
  jobs?: ToolCardJob[];
  proposal?: ToolCardProposal | null;
  chips?: ToolCardChip[];
  proposal_code?: string | null;
  choices?: ToolCardChoices | null;
  block?: Record<string, unknown> | null;
}

export interface StreamingOptions {
  chatId: number;
  messageId: number;
  token: string;
  enableSearch?: boolean;
  productId?: number | null;
  onDelta: (delta: string, fullResponse: string) => void;
  onComplete: (messageId: number, content: string) => void;
  onError: (error: string) => void;
  onSearchProgress?: (message: string) => void;
  onSearchResult?: (result: SearchResult) => void;
  onSearchError?: (error: string) => void;
  onThinking?: (delta: string, fullThinking: string) => void;
  onToolEvent?: (event: ToolStreamEvent) => void;
}

/**
 * GPT-5 Streaming Service
 *
 * EventSourceを使用してGPT-5 Responses APIからのストリーミングレスポンスを処理
 */
class StreamingService {
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 2000; // 2秒
  private fullResponse = ''; // 累積的なレスポンステキスト
  private fullThinking = ''; // 累積的なthinking（reasoning summary）テキスト
  private isCompleted = false; // ストリーミング完了フラグ
  private lastEventId: string | null = null; // 最後に受信したイベントID（再接続用）
  private isReconnecting = false; // 再接続中フラグ

  /**
   * ストリーミング接続を開始
   * @param options ストリーミングオプション
   * @param preserveState 再接続時にfullResponseを保持するかどうか
   */
  startStreaming(options: StreamingOptions, preserveState: boolean = false): void {
    const { chatId, messageId, token, enableSearch, productId, onDelta, onComplete, onError, onSearchProgress, onSearchResult, onSearchError, onThinking, onToolEvent } = options;

    // 既存の接続をクローズ（stopStreamingはfullResponseをリセットしない）
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // 新規接続の場合のみ状態をリセット（再接続時は保持）
    if (!preserveState) {
      this.fullResponse = '';
      this.fullThinking = '';
      this.lastEventId = null;
    }
    this.isCompleted = false;
    this.isReconnecting = false;

    try {
      // EventSource URLを構築（クエリパラメータでトークン認証）
      // SSEルートは/apiプレフィックスを除外してLaravelの自動apiミドルウェア適用を回避
      // 末尾の/apiのみを削除（https://api.tasky-ai.comの/apiを誤って削除しないように正規表現使用）
      const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:8080';
      let url = `${baseUrl}/chats/stream?chat_id=${chatId}&message_id=${messageId}&token=${encodeURIComponent(token)}`;

      // 検索と商品選択のパラメータを追加
      if (enableSearch !== undefined) {
        url += `&enable_search=${enableSearch ? '1' : '0'}`;
      }
      if (productId !== undefined && productId !== null) {
        url += `&product_id=${productId}`;
      }
      // 再接続時は最後のイベントIDを渡す（サーバーがそのIDより後のイベントを送信）
      if (this.lastEventId !== null) {
        url += `&lastEventId=${this.lastEventId}`;
      }

      console.log('StreamingService: Starting EventSource connection', {
        chatId,
        messageId,
        enableSearch,
        productId,
        baseUrl,
        isReconnecting: preserveState,
        lastEventId: this.lastEventId,
        currentFullResponseLength: this.fullResponse.length,
        url: url.replace(token, '***')
      });

      this.eventSource = new EventSource(url);

      // メッセージ受信
      this.eventSource.addEventListener('message', (event) => {
        try {
          // SSEイベントIDを追跡（再接続時に使用）
          if (event.lastEventId) {
            this.lastEventId = event.lastEventId;
          }

          // 生のSSEデータをログ出力
          console.log('🔵 SSE Raw Data:', event.data, 'eventId:', event.lastEventId);

          const data = JSON.parse(event.data);

          console.log('🟢 SSE Parsed:', {
            type: data.type,
            delta: data.delta,
            delta_length: data.delta?.length || 0,
            content: data.content?.substring(0, 100),
            message_id: data.message_id,
            lastEventId: event.lastEventId,
            fullData: data
          });

          switch (data.type) {
            case 'connected':
              // 接続確認イベント（無視してOK）
              console.log('StreamingService: Connected to stream', {
                chatId: data.chat_id,
                messageId: data.message_id
              });
              break;

            case 'started':
              // ストリーミング開始イベント（GPT-5が処理を開始したことを示す）
              console.log('StreamingService: Streaming started', {
                timestamp: data.timestamp
              });
              break;

            case 'delta':
              // テキストdeltaを処理
              if (data.delta) {
                // サーバーから送られた累積content（存在する場合）とローカルのfullResponseを比較
                // サーバーのcontentの方が正確なので、差異があればサーバーのcontentを使用
                if (data.content && data.content.length > this.fullResponse.length) {
                  // サーバーのcontentが長い場合、サーバーのcontentで上書き（欠落したdeltaを補完）
                  console.log('StreamingService: Using server content (delta loss detected)', {
                    local_length: this.fullResponse.length,
                    server_length: data.content.length,
                    diff: data.content.length - this.fullResponse.length
                  });
                  this.fullResponse = data.content;
                } else {
                  // 通常のdelta累積
                  this.fullResponse += data.delta;
                }

                console.log('StreamingService: Delta received', {
                  delta_length: data.delta.length,
                  fullResponse_length: this.fullResponse.length,
                  delta_preview: data.delta.substring(0, 50)
                });

                // 累積されたfullResponseをコールバックに渡す
                onDelta(data.delta, this.fullResponse);
              }
              break;

            case 'thinking':
              // GPT-5のreasoning summary（thinking）を処理
              if (data.delta) {
                this.fullThinking += data.delta;

                console.log('StreamingService: Thinking received', {
                  delta_length: data.delta.length,
                  fullThinking_length: this.fullThinking.length,
                  thinking_preview: data.delta.substring(0, 50)
                });

                // コールバックが設定されている場合のみ呼び出し
                if (onThinking) {
                  onThinking(data.delta, this.fullThinking);
                }
              }
              break;

            case 'complete':
              // ストリーミング完了
              // サーバーから送られたcontentとローカルのfullResponseを比較し、より長い方を使用
              const serverContent = data.content || '';
              const finalContent = serverContent.length >= this.fullResponse.length
                ? serverContent
                : this.fullResponse;

              console.log('StreamingService: Stream completed', {
                messageId: data.message_id,
                server_content_length: serverContent.length,
                local_fullResponse_length: this.fullResponse.length,
                final_content_length: finalContent.length,
                usedLocal: finalContent === this.fullResponse
              });
              this.isCompleted = true; // 完了フラグを立てる（エラーハンドラでのリコネクト防止）
              onComplete(data.message_id, finalContent);
              this.stopStreaming();
              break;

            case 'error':
              // エラー発生
              console.error('StreamingService: Server error', data.message);
              onError(data.message || 'AI応答の生成中にエラーが発生しました。');
              this.stopStreaming();
              break;

            case 'search.progress':
              // 検索進捗
              console.log('StreamingService: Search progress', data.message);
              if (onSearchProgress) {
                onSearchProgress(data.message || '検索中...');
              }
              break;

            case 'search.result':
              // 検索結果
              console.log('StreamingService: Search result', {
                index: data.index,
                title: data.title
              });
              if (onSearchResult) {
                onSearchResult({
                  index: data.index || 0,
                  title: data.title || '',
                  url: data.url || '',
                  snippet: data.snippet || ''
                });
              }
              break;

            case 'search.error':
              // 検索エラー
              console.error('StreamingService: Search error', data.message);
              if (onSearchError) {
                onSearchError(data.message || '検索中にエラーが発生しました。');
              }
              break;

            case 'tool_use':
            case 'tool_result':
              // チャットツール実行イベント（実行中/進捗/完了）
              console.log('StreamingService: Tool event', {
                type: data.type,
                name: data.name,
                call_id: data.call_id,
                status: data.status,
                ok: data.ok
              });
              if (onToolEvent) {
                onToolEvent(data as ToolStreamEvent);
              }
              break;

            default:
              console.warn('StreamingService: Unknown event type', data.type);
          }
        } catch (error) {
          console.error('StreamingService: Failed to parse event data', error);
          onError('メッセージの処理中にエラーが発生しました。');
        }
      });

      // 接続オープン
      this.eventSource.addEventListener('open', () => {
        console.log('StreamingService: EventSource connection opened');
        this.reconnectAttempts = 0; // リセット
      });

      // エラーハンドリング
      this.eventSource.addEventListener('error', (error) => {
        // 完了後のエラーは無視（サーバーが正常に接続を閉じた場合も発生する）
        if (this.isCompleted) {
          console.log('StreamingService: Ignoring error after completion (normal close)');
          this.stopStreaming();
          return;
        }

        console.error('StreamingService: EventSource error', error);

        // 再接続を試みる
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`StreamingService: Reconnecting (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`, {
            preservedFullResponseLength: this.fullResponse.length,
            lastEventId: this.lastEventId
          });

          // EventSourceを閉じる（fullResponseは保持）
          if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
          }
          this.isReconnecting = true;

          setTimeout(() => {
            // preserveState: true で再接続（fullResponseとlastEventIdを保持）
            this.startStreaming(options, true);
          }, this.reconnectDelay);
        } else {
          console.error('StreamingService: Max reconnect attempts reached');
          onError('接続が切断されました。もう一度お試しください。');
          this.stopStreaming();
        }
      });

    } catch (error) {
      console.error('StreamingService: Failed to start streaming', error);
      onError('ストリーミング接続の開始に失敗しました。');
    }
  }

  /**
   * ストリーミング接続を停止
   */
  stopStreaming(): void {
    if (this.eventSource) {
      console.log('StreamingService: Closing EventSource connection');
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * 接続状態を取得
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }
}

export default new StreamingService();
